<template>
  <base-form-field v-model="refiUnderpaymentConsent" :schema="schema" :disabled="submittingData" />
  <next-button
    text="Submit"
    @click="saveRefiUnderpaymentConsent"
    :disabled="submittingData"
    :loading="submittingData"
  ></next-button>
  <div class="error" v-show="error">
    <p>
      An error occurred while processing your acknowledgement. Please try again later. If the issue
      persists, please contact our support team.
    </p>
  </div>
</template>
<script setup lang="ts">
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import type { FieldSchema } from "@/models/form";
import type { Applicant } from "@/models/application";
import type { Stipulation } from "@/models/opal";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import { $api } from "@/services/api";
import useValidation from "@/use/validation";
import type { ConsentForm } from "@/models/consents";

const { validate } = useValidation();

const store = useStore();

interface Props {
  applicant: Applicant;
  stip: Stipulation;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
}>();

let consents = ref({} as any);

const error = ref<boolean>(false);
const submittingData = ref<boolean>(false);
const refiUnderpaymentConsent = ref<boolean | undefined>(undefined);
const refiUnderpaymentConsentTypeId = computed<number>(
  () =>
    store.state.lookupsModule?.consentTypes?.find(
      (ct: any) => ct.name?.trim()?.toLowerCase() === "RefiUnderpayment".toLowerCase(),
    )?.id,
);
const schema = reactive<FieldSchema>({
  type: "checkbox",
  hideLabel: true,
  label: "I agree",
  errorMessage: "You must agree to submit",
  required: true,
} as FieldSchema);

function getConsent(consents: ConsentForm[], consentTypeId: number) {
  return (consents || [])?.find((q: ConsentForm) => q.consentTypeId === consentTypeId);
}
async function getConsents() {
  await store.dispatch("lookupsModule/getConsentTypes");
  const questions = await $api.consents.getAllConsents(props?.applicant?.id);
  if (questions.length > 0) {
    const refiUnderpaymentConsent = getConsent(questions, refiUnderpaymentConsentTypeId.value);

    consents.value.refiUnderpaymentConsentId = refiUnderpaymentConsentTypeId.value;
    consents.value.refiUnderpaymentConsent = refiUnderpaymentConsent?.consentGiven;
  }
}
async function saveRefiUnderpaymentConsent() {
  error.value = false;
  validate(schema, refiUnderpaymentConsent.value);

  if (schema.valid) {
    submittingData.value = true;

    const updateConsentStipulationRequest = {
      applicationId: store?.state?.appData?.application?.id || 0,
      applicantId: props?.applicant?.id || 0,
      stipulationId: props?.stip?.id,
      consentGiven: refiUnderpaymentConsent.value,
      updatedByUserId: props?.applicant?.userId,
    };

    const updateConsentStipulationResponse = await $api.applicant.updateApplicantConsentStipulation(
      updateConsentStipulationRequest,
    );

    if (
      updateConsentStipulationResponse?.stipulationId &&
      updateConsentStipulationResponse?.stipulationId > 0
    ) {
      let updatedStip = props.stip;
      updatedStip.stipulationStatusId = updateConsentStipulationResponse.stipulationStatusId;
      updatedStip.stipulationStatusName = updateConsentStipulationResponse.stipulationStatusName;
      emit("success", updatedStip);
      submittingData.value = false;
    } else {
      error.value = true;
      submittingData.value = false;
    }
  } else {
    submittingData.value = false;
  }
}

onMounted(() => {
  getConsents();
});
</script>
<style>
.error {
  color: #8b0e04;
}
</style>
